import { isPlatformBrowser } from "@angular/common";
import { Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { selectRequestCountry } from "@app/store/selectors/app/app.selectors";
import { MainState } from "@app/store/state";
import { BaseService } from "@core/services/base.service";
import { CookieConsentService } from "@core/services/cookie-consent/cookie-consent.service";
import { LoadingService } from "@core/services/loading.service";
import { UserSubscriptionService } from "@core/services/user-subscription/user-subscription.service";
import { UtilsService } from "@core/services/utils/utils.service";
import { WindowRefService } from "@core/services/window-ref.service";
import { CookieConsentEnum } from "@core/types/cookie-consent.enum";
import { Store } from "@ngrx/store";
import { filter, map, take } from "rxjs/operators";

@Injectable({
  providedIn: "root"
})
export class AdManagerService extends BaseService {
  private _adSlots: { [key: string]: any } = {};
  private _adSlotConfigs: { [key: string]: { configName: string; adUnitPath: string; size: any[] } } = {};
  private readonly _isBrowser: boolean;
  private _publisherId = "47890729";
  private _adConfigurations = {
    rectangular: {
      adUnitPath: `/${this._publisherId}/astrobin-native-responsive-rectangular`,
      divId: "div-gpt-ad-1603646272754-0",
      adSize: ["fluid"]
    },
    wide: {
      adUnitPath: `/${this._publisherId}/astrobin-native-responsive-wide`,
      divId: "div-gpt-ad-1726208728627-0",
      adSize: ["fluid"]
    }
  };
  private _lastRequestTimestamp = 0;
  private _gptReady: Promise<void>;
  private _gptReadyResolve: () => void;

  constructor(
    public readonly loadingService: LoadingService,
    public readonly store$: Store<MainState>,
    public readonly windowRefService: WindowRefService,
    @Inject(PLATFORM_ID) public readonly platformId: Object,
    public readonly cookieConsentService: CookieConsentService,
    public readonly userSubscriptionService: UserSubscriptionService
  ) {
    super(loadingService);

    this._isBrowser = isPlatformBrowser(platformId);

    // Create a promise that resolves when GPT is ready
    this._gptReady = new Promise<void>(resolve => {
      this._gptReadyResolve = resolve;
    });

    if (this._isBrowser) {
      this._initGooglePublisherTag();
    } else {
      // Resolve immediately on server side
      this._gptReadyResolve();
    }
  }

  getAdConfig(name: string) {
    const config = this._adConfigurations[name] || null;
    return config;
  }

  defineAdSlot(configName: string, adUnitPath: string, size: any[], divId: string): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      if (this._lastRequestTimestamp && Date.now() - this._lastRequestTimestamp < 100) {
        reject("Too many requests");
        return;
      }

      this._lastRequestTimestamp = Date.now();

      if (this._isBrowser) {
        // Wait for GPT to be ready
        await this._gptReady;

        const nativeWindow = this.windowRefService.nativeWindow as any;
        if (nativeWindow && nativeWindow.googletag && nativeWindow.googletag.cmd) {
          nativeWindow.googletag.cmd.push(() => {
            const slot = nativeWindow.googletag.defineSlot(adUnitPath, size, divId);
            if (slot) {
              slot.setTargeting("format", [configName]).addService(nativeWindow.googletag.pubads());
              this._adSlots[divId] = slot;
              // Store the configuration for later use in refresh
              this._adSlotConfigs[divId] = { configName, adUnitPath, size };
              resolve();
            } else {
              reject("Failed to define slot");
            }
          });
        } else {
          reject("Google Publisher Tag not loaded");
        }
      }
    });
  }

  hasAdSlot(divId: string): boolean {
    return !!this._adSlots[divId];
  }

  displayAd(divId: string): Promise<boolean> {
    return new Promise(async resolve => {
      if (this._isBrowser) {
        // Wait for GPT to be ready
        await this._gptReady;

        const nativeWindow = this.windowRefService.nativeWindow as any;
        if (nativeWindow) {
          const slot = this._adSlots[divId];
          if (!slot) {
            resolve(false);
            return;
          }

          const renderListener = event => {
            if (event.slot === slot) {
              nativeWindow.googletag.pubads().removeEventListener("slotRenderEnded", renderListener);

              if (event.isEmpty) {
                resolve(false);
              } else {
                // Add slotOnload listener only if we're expecting an ad
                const loadListener = loadEvent => {
                  if (loadEvent.slot === slot) {
                    nativeWindow.googletag.pubads().removeEventListener("slotOnload", loadListener);
                    resolve(true);
                  }
                };
                nativeWindow.googletag.pubads().addEventListener("slotOnload", loadListener);
              }
            }
          };

          if (nativeWindow.googletag && nativeWindow.googletag.cmd) {
            nativeWindow.googletag.pubads().addEventListener("slotRenderEnded", renderListener);

            nativeWindow.googletag.cmd.push(() => {
              // When initial load is disabled, we must call display first
              nativeWindow.googletag.display(divId);
            });

            // Then refresh in a separate command to ensure proper sequencing
            nativeWindow.googletag.cmd.push(() => {
              nativeWindow.googletag.pubads().refresh([slot]);
            });
          } else {
            resolve(false);
          }
        } else {
          resolve(false);
        }
      } else {
        resolve(false);
      }
    });
  }

  refreshAd(divId: string): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      if (this._lastRequestTimestamp && Date.now() - this._lastRequestTimestamp < 100) {
        reject("Too many requests");
        return;
      }

      this._lastRequestTimestamp = Date.now();

      if (this._isBrowser) {
        // Wait for GPT to be ready
        await this._gptReady;

        const existingSlot = this._adSlots[divId];

        if (!existingSlot) {
          resolve(false);
          return;
        }

        const nativeWindow = this.windowRefService.nativeWindow as any;
        if (nativeWindow && nativeWindow.googletag && nativeWindow.googletag.cmd) {
          try {
            const renderListener = event => {
              if (event.slot === existingSlot) {
                nativeWindow.googletag.pubads().removeEventListener("slotRenderEnded", renderListener);

                if (event.isEmpty) {
                  resolve(false);
                } else {
                  const loadListener = loadEvent => {
                    if (loadEvent.slot === existingSlot) {
                      nativeWindow.googletag.pubads().removeEventListener("slotOnload", loadListener);
                      resolve(true);
                    }
                  };
                  nativeWindow.googletag.pubads().addEventListener("slotOnload", loadListener);
                }
              }
            };

            nativeWindow.googletag.pubads().addEventListener("slotRenderEnded", renderListener);

            nativeWindow.googletag.cmd.push(() => {
              nativeWindow.googletag.pubads().refresh([existingSlot]);
            });
          } catch (error) {
            resolve(false);
          }
        } else {
          resolve(false);
        }
      } else {
        resolve(false);
      }
    });
  }

  destroyAdSlot(divId: string): Promise<void> {
    return new Promise<void>(resolve => {
      if (this._isBrowser) {
        const nativeWindow = this.windowRefService.nativeWindow as any;
        if (nativeWindow && nativeWindow.googletag && nativeWindow.googletag.cmd) {
          nativeWindow.googletag.cmd.push(() => {
            const slot = this._adSlots[divId];
            if (slot) {
              nativeWindow.googletag.destroySlots([slot]);
              delete this._adSlots[divId];
              delete this._adSlotConfigs[divId];
            }
            resolve();
          });
        } else {
          // If googletag is not ready, just clean up our references
          delete this._adSlots[divId];
          delete this._adSlotConfigs[divId];
          resolve();
        }
      } else {
        resolve();
      }
    });
  }

  private _initGooglePublisherTag() {
    const nativeWindow = this.windowRefService.nativeWindow as any;
    if (nativeWindow) {
      const initializeGPT = () => {
        this.store$
          .select(selectRequestCountry)
          .pipe(
            filter(requestCountry => !!requestCountry),
            take(1),
            map(requestCountry => {
              if (requestCountry === "UNKNOWN") {
                requestCountry = "US";
              }

              const isGDPRCountry = UtilsService.isGDPRCountry(requestCountry);
              const hasCookieConsent = this.cookieConsentService.cookieGroupAccepted(CookieConsentEnum.ADVERTISING);

              nativeWindow.googletag = nativeWindow.googletag || {};
              nativeWindow.googletag.cmd = nativeWindow.googletag.cmd || [];

              // Set page-level configuration before any GPT operations
              nativeWindow.googletag.secureSignalProviders = nativeWindow.googletag.secureSignalProviders || [];

              nativeWindow.googletag.cmd.push(() => {
                // Disable initial load to have more control over ad loading
                nativeWindow.googletag.pubads().disableInitialLoad();
                nativeWindow.googletag.pubads().enableSingleRequest();

                // Set privacy settings to disable Protected Audience and Topics API
                const privacySettings: any = {
                  // Disable Protected Audience (formerly FLEDGE) to prevent attestation issues
                  runAdAuction: false,
                  // Also disable Topics API which can cause similar issues
                  topics: false
                };

                if (isGDPRCountry && !hasCookieConsent) {
                  privacySettings.limitedAds = true;
                  privacySettings.nonPersonalizedAds = true;
                  privacySettings.restrictDataProcessing = true;
                }

                nativeWindow.googletag.pubads().setPrivacySettings(privacySettings);

                // Set additional targeting to opt out of interest-based ads
                nativeWindow.googletag.pubads().setTargeting("ltd", "true");

                // Set subscription tier targeting
                this.userSubscriptionService
                  .getSubscriptionTier$()
                  .pipe(take(1))
                  .subscribe(tier => {
                    nativeWindow.googletag.pubads().setTargeting("subscription-tier", tier);
                  });

                nativeWindow.googletag.enableServices();

                // Resolve the promise to indicate GPT is ready
                this._gptReadyResolve();
              });
            })
          )
          .subscribe();
      };

      // Check if GPT is already loaded
      if (nativeWindow.googletag && nativeWindow.googletag.apiReady) {
        initializeGPT();
      } else {
        // Function to load GPT script with retry logic
        const loadGPTScript = (retryCount = 0, maxRetries = 3) => {
          const script = document.createElement("script");
          script.src = "https://pagead2.googlesyndication.com/tag/js/gpt.js";
          script.async = true;

          script.onload = () => {
            initializeGPT();
          };

          script.onerror = error => {
            // Remove the failed script element
            if (script.parentNode) {
              script.parentNode.removeChild(script);
            }

            // Retry if we haven't exceeded max retries
            if (retryCount < maxRetries - 1) {
              const delay = (retryCount + 1) * 2000; // 2s, 4s, 6s
              if (this._isBrowser) {
                setTimeout(() => {
                  loadGPTScript(retryCount + 1, maxRetries);
                }, delay);
              }
            } else {
              const lastScript = document.createElement("script");
              lastScript.src = "https://pagead2.googlesyndication.com/tag/js/gpt.js";
              lastScript.async = true;
              lastScript.onload = () => {
                initializeGPT();
              };
              document.head.appendChild(lastScript);
            }
          };

          document.head.appendChild(script);
        };

        // Check service worker status but don't wait too long
        if ("serviceWorker" in navigator) {
          // Set a timeout to load GPT anyway if service worker takes too long
          let swTimeout;
          if (this._isBrowser) {
            swTimeout = setTimeout(() => {
              loadGPTScript();
            }, 2000); // 2 second timeout
          }

          if (navigator.serviceWorker.controller) {
            // Service worker is already controlling the page
            if (swTimeout) {
              clearTimeout(swTimeout);
            }
            loadGPTScript();
          } else {
            // Wait for service worker to be ready, but with timeout
            navigator.serviceWorker.ready
              .then(() => {
                if (swTimeout) {
                  clearTimeout(swTimeout);
                }
                loadGPTScript();
              })
              .catch(error => {
                if (swTimeout) {
                  clearTimeout(swTimeout);
                }
                loadGPTScript();
              });
          }
        } else {
          // No service worker support, load immediately
          loadGPTScript();
        }
      }
    }
  }
}
